import { AwardOrderRequest, OfferResponse, OffersToAccept, OrderResponse } from "../../types";

/**
 * Pure award-payload + comparison-matrix formatting for the SINGLE-order award panel.
 *
 * Extracted out of `Award.tsx` so the AWARD-PAYLOAD PARITY contract can be locked with a
 * unit test: the object handed to `onAcceptOffer` (→ `PUT /offer/list/select`) MUST stay
 * byte-equivalent to the pre-Carbon behaviour. Do not change these shapes without flagging.
 */

export type FormattedOffer = OfferResponse & {
  totalCost: number;
  isBest: boolean;
};

/**
 * Builds the comparison-matrix column model: each offer gets a `totalCost`
 * (price × requested quantity) and an `isBest` flag (cheapest total wins),
 * then the list is sorted by material name. Mirrors `Award.tsx`'s original
 * `formattedOffers` useMemo exactly (including the empty-array guard and the
 * initial-value-less `reduce`, which throws on a non-empty-but-guarded path
 * only as the original did).
 */
export const formatOffers = (offers: OfferResponse[]): FormattedOffer[] => {
  if (!offers) return [];

  const offersWithTotalCost = offers.map((offer) => ({
    ...offer,
    totalCost: offer.price * offer.order.quantity,
  }));

  const bestOffer = offersWithTotalCost.reduce((prev, current) =>
    prev.totalCost < current.totalCost ? prev : current
  );

  const offersWithBestFlag = offersWithTotalCost.map((offer) => ({
    ...offer,
    isBest: offer.id === bestOffer.id,
  }));

  return offersWithBestFlag
    .slice()
    .sort((a, b) =>
      a.order.material.globalMaterialName.localeCompare(
        b.order.material.globalMaterialName
      )
    );
};

/**
 * Builds the `AwardOrderRequest[]` passed to `onAcceptOffer`. Byte-equivalent
 * to `Award.tsx`'s original `onAwardTender` mapping — see AWARD-PAYLOAD PARITY.
 */
export const buildSingleAwardPayload = (
  offersToAccept: OffersToAccept[],
  order: OrderResponse,
  offerSelectionComment: string,
  offerSelectionFileUrl = ""
): AwardOrderRequest[] =>
  offersToAccept.map((offer) => ({
    offer: {
      ...offer.offer,
      price: parseFloat(String(offer.price)),
      quantity: parseFloat(String(offer.volume)),
      oldQuantity: order.quantity,
      oldPrice: offer.offer.price,
      selected: true,
    },
    offerSelectionComment,
    offerSelectionFileUrl,
    replace: false,
  })) as unknown as AwardOrderRequest[];

/**
 * What was awarded, rebuilt from the offers themselves, in the reducer's selection shape: an
 * award marks each chosen offer `selected` and stores its awarded volume as `quantity`; the
 * price is the offered one. Used to show a closed award read-only.
 */
export const awardedSelection = (
  offers: (OfferResponse | undefined)[]
): OffersToAccept[] =>
  offers
    .filter((offer): offer is OfferResponse => Boolean(offer?.selected))
    .map((offer) => ({
      id: offer.id,
      volume: offer.quantity ?? offer.order.quantity,
      price: offer.price,
      orderId: offer.order.id,
      material: offer.order.material.globalMaterialName,
      offer,
    })) as unknown as OffersToAccept[];
